import React, { useEffect, useState } from 'react';
import { 
  Card, 
  Typography, 
  Row, 
  Col, 
  Tag, 
  Button, 
  Divider, 
  message, 
  Table,
  Space,
  Avatar,
  Descriptions,
  Timeline,
  Modal,
  Form,
  Input,
  DatePicker,
  Tooltip,
  Progress,
  Statistic,
  Alert,
  Badge,
  Tabs
} from 'antd';
import {
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SendOutlined,
  DownloadOutlined,
  EditOutlined,
  CalendarOutlined,
  TeamOutlined,
  ExclamationCircleOutlined,
  ArrowLeftOutlined,
  StarOutlined,
  SafetyCertificateOutlined,
  HistoryOutlined,
  DollarOutlined
} from '@ant-design/icons';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getAssignment, getAssignmentDocunebt, getAssginmentTimesheet, approveAssginmentTimesheet, updateAssignment ,getEmployee} from '../../api';
import './CandidateDetails.css';

const { Title, Text, Link, Paragraph } = Typography;
const { TabPane } = Tabs;
const { confirm } = Modal;

const CandidateDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { candidateId } = useParams();
  const [candidate, setCandidate] = useState(location.state?.candidate || null);
  const [documents, setDocuments] = useState([]);
  const [timesheet, setTimesheet] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [status, setStatus] = useState(location.state?.candidate?.status || '');
  const [loading, setLoading] = useState(!location.state?.candidate);

  useEffect(() => {
    const fetchAssignmentData = async () => {
      // If we already have candidate data from props, only fetch additional data
      if (candidate && !candidateId) {
        // We have candidate from props but no candidateId, fetch additional data using candidate._id
        try {
          setLoading(true);
          const document = await getAssignmentDocunebt(candidate._id);
          setDocuments(document.data?.documents);
          
          const timesheetResponse = await getAssginmentTimesheet(candidate._id);
          setTimesheet(timesheetResponse.data);
        } catch (error) {
          console.error('Failed to fetch additional assignment data:', error);
          message.error('Failed to load additional candidate data');
        } finally {
          setLoading(false);
        }
        return;
      }

      // If we don't have candidateId, we can't fetch from API
      if (!candidateId) {
        setLoading(false);
        return;
      }
      
      // If we already have candidate data from props, try to fetch from API but fallback to props on error
      if (candidate) {
        try {
          // Try to fetch fresh data from API
          const candidateResponse = await getEmployee(candidateId);
          const candidateData = candidateResponse.data;
          setCandidate(candidateData);
          setStatus(candidateData.status);
        } catch (error) {
          console.warn('Failed to fetch candidate from API, using props data:', error);
          // Keep existing candidate data from props
        }
      } else {
        // No candidate data from props, must fetch from API
        setLoading(true);
        try {
          const candidateResponse = await getAssignment(candidateId);
          const candidateData = candidateResponse.data;
          setCandidate(candidateData);
          setStatus(candidateData.status);
        } catch (error) {
          console.error('Failed to fetch candidate data from API:', error);
          message.error('Failed to load candidate data');
          setLoading(false);
          return;
        }
      }

      // Fetch additional data (documents and timesheet)
      try {
        const document = await getAssignmentDocunebt(candidateId);
        setDocuments(document.data?.documents);
        
        const timesheetResponse = await getAssginmentTimesheet(candidateId);
        setTimesheet(timesheetResponse.data);
      } catch (error) {
        console.error('Failed to fetch additional assignment data:', error);
        message.error('Failed to load additional candidate data');
      } finally {
        setLoading(false);
      }
    };

    fetchAssignmentData();
  }, [candidateId, candidate?._id]);

  const getStatusColor = (status) => {
    const colors = {
      'idle': 'blue',
      'submitted': 'orange',
      'application-rejected': 'red',
      'offer-rejected': 'red',
      'extend-offer-rejected': 'red',
      'offer-sent': 'purple',
      'extend-offer-sent': 'purple',
      'offer-accepted': 'green',
      'extend-offer-accepted': 'green',
      'ongoing': 'green',
      'compliance-in-progress': 'cyan',
      'ended': 'default'
    };
    return colors[status] || 'default';
  };

  const getStatusProgress = (status) => {
    const progress = {
      'idle': 10,
      'submitted': 25,
      'offer-sent': 50,
      'offer-accepted': 75,
      'ongoing': 90,
      'ended': 100,
      'application-rejected': 0,
      'offer-rejected': 0
    };
    return progress[status] || 0;
  };

  const handleApprove = async (record) => {
    try {
      await approveAssginmentTimesheet(record.assignment, { timecardIds: [record._id] });
      message.success('Timesheet approved successfully');
      setTimesheet(timesheet.map(item => 
        item._id === record._id ? { ...item, status: 'approved' } : item
      ));
    } catch (error) {
      message.error('Failed to approve timesheet');
    }
  };

  const handleBulkApprove = async () => {
    try {
      const id = candidateId || candidate?._id;
      if (!id) {
        message.error('Unable to identify candidate');
        return;
      }
      await approveAssginmentTimesheet(id, { timecardIds: selectedRowKeys });
      message.success('Selected timesheets approved successfully');
      setTimesheet(timesheet.map(item => 
        selectedRowKeys.includes(item._id) ? { ...item, status: 'approved' } : item
      ));
      setSelectedRowKeys([]);
    } catch (error) {
      message.error('Failed to approve selected timesheets');
    }
  };



  const handleSendOffer = async () => {
    try {
      const id = candidateId || candidate?._id;
      if (!id) {
        message.error('Unable to identify candidate');
        return;
      }
      const res = await updateAssignment(id, { transition: 'offer' });
      message.success('Offer sent successfully');
      setStatus(res.data.assignment.status);
    } catch (error) {
      message.error('Failed to send offer');
    }
  };

  const handleReject = async () => {
    try {
      const id = candidateId || candidate?._id;
      if (!id) {
        message.error('Unable to identify candidate');
        return;
      }
      await updateAssignment(id, { transition: 'rejectApplication' });
      message.success('Candidate rejected successfully');
      setStatus('application-rejected');
    } catch (error) {
      message.error('Failed to reject candidate');
    }
  };

  const handleExtendOffer = async () => {
    try {
      const id = candidateId || candidate?._id;
      if (!id) {
        message.error('Unable to identify candidate');
        return;
      }
      await updateAssignment(id, { transition: 'extendOffer' });
      message.success('Extend offer sent successfully');
      setStatus('extend-offer-sent');
    } catch (error) {
      message.error('Failed to send extend offer');
    }
  };

  const renderActionButtons = () => {
    const buttonStyle = { minWidth: '120px', height: '40px' };
    
    switch (status) {
      case 'idle':
      case 'submitted':
        return (
          <Space size="middle">
            <Button 
              type="primary" 
              icon={<SendOutlined />}
              onClick={handleSendOffer}
              style={buttonStyle}
              loading={loading}
            >
              Send Offer
            </Button>
            <Button 
              danger 
              icon={<CloseCircleOutlined />}
              onClick={handleReject}
              style={buttonStyle}
              loading={loading}
            >
              Reject
            </Button>
          </Space>
        );

      case 'offer-sent':
        return (
          <Text type="secondary">Offer sent - waiting for candidate response</Text>
        );

      case 'offer-accepted':
      case 'ongoing':
        return (
          <Button 
            type="primary" 
            icon={<SendOutlined />}
            onClick={handleExtendOffer}
            style={buttonStyle}
            loading={loading}
          >
            Extend Offer
          </Button>
        );

      case 'offer-rejected':
      case 'extend-offer-sent':
        return (
          <Text type="secondary">No actions available</Text>
        );

      case 'extend-offer-accepted':
      case 'extend-offer-rejected':
      case 'compliance-in-progress':
      case 'ended':
      default:
        return (
          <Text type="secondary">No actions available for this status</Text>
        );
    }
  };

  const timesheetColumns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (text) => new Date(text).toLocaleDateString(),
    },
    {
      title: 'Time',
      key: 'time',
      render: (record) => (
        <div>
          <div><Text strong>In:</Text> {record.clockIn}</div>
          <div><Text strong>Out:</Text> {record.clockOut}</div>
        </div>
      ),
    },
    {
      title: 'Break',
      key: 'break',
      render: (record) => (
        <div>
          <div><Text strong>Start:</Text> {record.breakStart}</div>
          <div><Text strong>End:</Text> {record.breakEnd}</div>
        </div>
      ),
    },
    {
      title: 'Duration',
      key: 'duration',
      render: (record) => (
        <div>
          <div><Text strong>Shift:</Text> {record.shiftDuration}m</div>
          <div><Text strong>Break:</Text> {record.breakDuration}m</div>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (text) => (
        <Tag color={text === 'pending' ? 'orange' : 'green'}>
          {text.replace(/-/g, ' ').replace(/\b\w/g, char => char.toUpperCase())}
        </Tag>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (record) => (
        <Button 
          type="primary" 
          size="small"
          disabled={record.status === 'approved'} 
          onClick={() => handleApprove(record)}
        >
          {record.status === 'approved' ? 'Approved' : 'Approve'}
        </Button>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    getCheckboxProps: (record) => ({
      disabled: record.status === 'approved',
    }),
  };



  if (loading) {
    return (
      <div style={{ padding: '16px', background: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Card style={{ textAlign: 'center', padding: '48px' }}>
          <div style={{ marginBottom: '16px' }}>
            <Avatar size={64} icon={<UserOutlined />} style={{ backgroundColor: '#006971' }} />
          </div>
          <Title level={4}>Loading candidate details...</Title>
          <Text type="secondary">Please wait while we fetch the candidate information.</Text>
        </Card>
      </div>
    );
  }

  if (!loading && !candidate) {
    return (
      <div style={{ padding: '16px', background: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Card style={{ textAlign: 'center', padding: '48px' }}>
          <div style={{ marginBottom: '16px' }}>
            <ExclamationCircleOutlined style={{ fontSize: '48px', color: '#ff4d4f' }} />
          </div>
          <Title level={4}>Candidate not found</Title>
          <Text type="secondary">
            The candidate you're looking for doesn't exist, the API is unavailable, or you don't have permission to view it.
          </Text>
          <div style={{ marginTop: '24px' }}>
            <Button type="primary" onClick={() => navigate('/employer/job-management')}>
              Back to Assignments
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: '16px', background: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Card style={{ marginBottom: '24px', borderRadius: '12px' }}>
        <Row justify="space-between" align="top">
          <Col>
            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={() => navigate(-1)}
              style={{ marginBottom: '16px' }}
            >
              Back
            </Button>
            
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
              <Avatar 
                size={80} 
                icon={<UserOutlined />} 
                style={{ backgroundColor: '#006971' }}
              />
              <div style={{ flex: 1 }}>
                <Title level={2} style={{ margin: 0, color: '#006971' }}>
                  {candidate.employee?.fullName || 'N/A'}
                </Title>
                <div style={{ marginTop: '8px', marginBottom: '16px' }}>
                  <Tag 
                    color={getStatusColor(status)} 
                    style={{ fontSize: '14px', padding: '4px 12px' }}
                  >
                    {status.replace(/-/g, ' ').replace(/\b\w/g, char => char.toUpperCase())}
                  </Tag>
                  <Text type="secondary" style={{ marginLeft: '12px' }}>
                    Managed by {candidate.agency || 'N/A'}
                  </Text>
                </div>
                
                <Row gutter={[24, 8]}>
                  <Col>
                    <Space>
                      <MailOutlined style={{ color: '#666' }} />
                      <Text>{candidate.employee?.email || 'N/A'}</Text>
                    </Space>
                  </Col>
                  <Col>
                    <Space>
                      <PhoneOutlined style={{ color: '#666' }} />
                      <Text>{candidate.employee?.phone || 'N/A'}</Text>
                    </Space>
                  </Col>
                  <Col>
                    <Space>
                      <EnvironmentOutlined style={{ color: '#666' }} />
                      <Text>
                        {`${candidate?.employee?.address?.city || 'N/A'}, ${candidate?.employee?.address?.state || 'N/A'}`}
                      </Text>
                    </Space>
                  </Col>
                </Row>
              </div>
            </div>
          </Col>
          
          <Col>
            <div style={{ textAlign: 'right', marginBottom: '16px' }}>
              <Text type="secondary">Application Progress</Text>
              <Progress 
                percent={getStatusProgress(status)} 
                strokeColor="#006971"
                style={{ marginTop: '8px',marginLeft: '8px', width: '200px' }}
              />
            </div>
            {renderActionButtons()}
          </Col>
        </Row>
      </Card>

             {/* Main Content - Single Column */}
       <Card>
         <Tabs defaultActiveKey="1">
           {/* Overview Tab */}
           <TabPane tab={<span><UserOutlined /> Overview</span>} key="1">
             <div style={{ marginBottom: '24px' }}>
               <Title level={4}>Personal Information</Title>
               <Descriptions bordered size="small" column={2}>
                 <Descriptions.Item label="Full Name">
                   {candidate.employee?.fullName || 'N/A'}
                 </Descriptions.Item>
                 <Descriptions.Item label="Email">
                   {candidate.employee?.email || 'N/A'}
                 </Descriptions.Item>
                 <Descriptions.Item label="Phone">
                   {candidate.employee?.phone || 'N/A'}
                 </Descriptions.Item>
                 <Descriptions.Item label="Status">
                   <Tag color={getStatusColor(status)}>
                     {status.replace(/-/g, ' ').replace(/\b\w/g, char => char.toUpperCase())}
                   </Tag>
                 </Descriptions.Item>
                 <Descriptions.Item label="Address" span={2}>
                   {`${candidate?.employee?.address?.street || ''}, ${candidate?.employee?.address?.city || 'N/A'}, ${candidate?.employee?.address?.state || 'N/A'} ${candidate?.employee?.address?.zipCode || ''}`}
                 </Descriptions.Item>
               </Descriptions>
             </div>

             {/* Professional Skills */}
             <div style={{ marginBottom: '24px' }}>
               <Title level={4}>Professional Skills</Title>
               <div>
                 {candidate?.employee?.units?.length > 0 ? (
                   candidate.employee?.units.map((unit, index) => (
                     <Tag color="cyan" key={index} style={{ margin: '4px 2px' }}>
                       {unit}
                     </Tag>
                   ))
                 ) : (
                   <Text type="secondary">No units specified</Text>
                 )}
               </div>
             </div>

             {/* Documents */}
             <div style={{ marginBottom: '24px' }}>
               <Title level={4}>Documents</Title>
               {documents?.length > 0 ? (
                 <div>
                   {documents.map((doc, index) => (
                     <div key={index} style={{ marginBottom: '8px' }}>
                       <Button 
                         type="link" 
                         icon={<FileTextOutlined />}
                         href={doc.url} 
                         target="_blank" 
                         rel="noopener noreferrer"
                         style={{ padding: 0, height: 'auto' }}
                       >
                         {doc.docType}
                       </Button>
                     </div>
                   ))}
                 </div>
               ) : (
                 <Text type="secondary">No documents available</Text>
               )}
             </div>

             {/* Time Off */}
             <div style={{ marginBottom: '24px' }}>
               <Title level={4}>Off Days</Title>
                                {candidate?.timeOffs?.length > 0 ? (
                 <div>
                   {candidate.timeOffs?.map((timeOff, index) => (
                     <Text key={index} style={{ display: 'block', marginBottom: '4px' }}>
                       {`${new Date(timeOff).toLocaleDateString()}`}
                     </Text>
                   ))}
                 </div>
               ) : (
                 <Text type="secondary">No off days available</Text>
               )}
             </div>

             {candidate?.coversheet && (
               <div>
                 <Title level={4}>Cover Letter</Title>
                 <Card size="small" style={{ backgroundColor: '#fafafa' }}>
                   <Paragraph>{candidate.coversheet}</Paragraph>
                 </Card>
               </div>
             )}
           </TabPane>

           {/* Timesheets Tab */}
           <TabPane 
             tab={
               <Badge count={timesheet.length} showZero>
                 <span><ClockCircleOutlined /> Timesheets</span>
               </Badge>
             } 
             key="2"
           >
             <div style={{ marginBottom: '16px' }}>
               <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
                 <Space>
                   <Statistic
                     title="Total Hours"
                     value={timesheet.reduce((sum, item) => sum + (item.shiftDuration || 0), 0)}
                     suffix="mins"
                     valueStyle={{ fontSize: '16px' }}
                   />
                   <Statistic
                     title="Approved"
                     value={timesheet.filter(item => item.status === 'approved').length}
                     suffix={`/ ${timesheet.length}`}
                     valueStyle={{ fontSize: '16px', color: '#52c41a' }}
                   />
                 </Space>
                 <Button 
                   type="primary" 
                   onClick={handleBulkApprove} 
                   disabled={selectedRowKeys.length === 0}
                   loading={loading}
                 >
                   Approve Selected ({selectedRowKeys.length})
                 </Button>
               </div>
             </div>

             <Table 
               rowSelection={rowSelection} 
               columns={timesheetColumns} 
               dataSource={timesheet} 
               rowKey="_id"
               pagination={{ pageSize: 10 }}
               scroll={{ x: 800 }}
             />
           </TabPane>

           {/* Activity Tab */}
           <TabPane tab={<span><HistoryOutlined /> Activity</span>} key="3">
             <Timeline>
               <Timeline.Item color="blue">
                 <Text strong>Application Submitted</Text>
                 <br />
                 <Text type="secondary">Candidate applied for the position</Text>
               </Timeline.Item>
               {status !== 'submitted' && status !== 'idle' && (
                 <Timeline.Item color={getStatusColor(status) === 'red' ? 'red' : 'green'}>
                   <Text strong>Status Updated</Text>
                   <br />
                   <Text type="secondary">
                     Status changed to {status.replace(/-/g, ' ')}
                   </Text>
                 </Timeline.Item>
               )}
             </Timeline>
           </TabPane>
         </Tabs>
       </Card>
    </div>
  );
};

export default CandidateDetails;
